/**
 * Smart Card Helper Functions
 *
 * Utility functions for the redesigned Smart Card Opportunities tiles.
 * Used to derive display values from mock/real data fields.
 */

/**
 * Determine benchmark status label from a percentage deviation.
 *   BETTER : deviation <= -5 (5%+ better than benchmark)
 *   ON PAR : deviation between -5 and +5
 *   WORSE  : deviation >= +5  (5%+ worse than benchmark)
 *
 * @param {number} pct - % deviation from benchmark (positive = worse)
 * @returns {'BETTER' | 'ON PAR' | 'WORSE'}
 */
export const getBenchmarkStatus = (pct) => {
  if (pct == null || isNaN(pct)) return 'ON PAR';
  if (pct >= 5) return 'WORSE';
  if (pct <= -5) return 'BETTER';
  return 'ON PAR';
};

/**
 * Normalise a Tableau "Cost Trend" text value ("Better", "Worse", "On Par")
 * to the canonical status tokens used throughout the UI.
 *
 * @param {string|null|undefined} trendText - raw text from the API
 * @returns {'BETTER' | 'ON PAR' | 'WORSE' | 'N/A'}
 */
export const parseCostTrend = (trendText) => {
  if (!trendText) return 'ON PAR';
  const upper = trendText.trim().toUpperCase();
  if (upper === 'NULL') return 'N/A';
  if (upper === 'BETTER') return 'BETTER';
  if (upper === 'WORSE') return 'WORSE';
  if (upper === 'ON PAR') return 'ON PAR';
  return 'ON PAR';
};

/**
 * Pick the worst-performing benchmark between Cost and Utilization.
 * "Worst" means the one with the higher positive deviation (further above benchmark).
 * If both are equal, cost takes priority.
 *
 * When the real `costTrend` text is available (from the Tableau SC data),
 * pass it as the third argument to bypass the numeric computation.
 *
 * @param {number}              costPct       - benchmarkCostPct (numeric)
 * @param {number}              utilPct       - benchmarkUtilPct (numeric)
 * @param {string|null|undefined} costTrendText - raw cost trend text from API ("Better" / "Worse" / "On Par" / "Null")
 * @returns {{ label: string, status: 'BETTER' | 'ON PAR' | 'WORSE' | 'N/A', value: number }}
 */
export const getWorstBenchmark = (costPct, utilPct, costTrendText) => {
  // When real cost trend text is available, use it directly for the cost metric.
  if (typeof costTrendText === 'string' && costTrendText.trim() !== '') {
    const costStatus = parseCostTrend(costTrendText);
    return {
      label: 'Cost',
      status: costStatus,
      value: costPct ?? 0,
    };
  }

  // When no cost trend text and all numeric benchmarks are absent, data is
  // unavailable from Tableau – display N/A.
  if (costPct == null && utilPct == null) {
    return { label: 'Cost', status: 'N/A', value: 0 };
  }

  const costVal = costPct ?? 0;
  const utilVal = utilPct ?? 0;

  if (costVal >= utilVal) {
    return {
      label: 'Cost',
      status: getBenchmarkStatus(costVal),
      value: costVal,
    };
  }
  return {
    label: 'Utilization',
    status: getBenchmarkStatus(utilVal),
    value: utilVal,
  };
};

/**
 * Format PEPM value for display.
 *   - Under 1,000 → "$123"
 *   - 1,000 – 999,999 → "$48.2K"
 *   - 1,000,000+ → "$1.2M"
 *
 * @param {number} value - raw PEPM dollar amount
 * @returns {string}
 */
export const formatPEPM = (value) => {
  if (value == null || isNaN(value)) return '$0';
  if (value < 1000) return `$${Math.round(value)}`;
  if (value < 1_000_000) {
    const k = value / 1000;
    return `$${k % 1 === 0 ? k : k.toFixed(1)}K`;
  }
  const m = value / 1_000_000;
  return `$${m % 1 === 0 ? m : m.toFixed(1)}M`;
};

/**
 * Derive the financial impact dollar-sign indicator using quartiles.
 * Sorts all savings ascending, then returns:
 *   $ for Q1, $$ for Q2, $$$ for Q3, $$$$ for Q4
 *
 * @param {number} savings        - this card's estimatedSavings
 * @param {number[]} allSavings   - all cards' estimatedSavings values
 * @returns {string} '$' | '$$' | '$$$' | '$$$$'
 */
export const getFinancialImpactDollars = (savings, allSavings = []) => {
  if (savings == null || allSavings.length === 0) return '$';
  if (!allSavings.includes(savings)) return '$';
  // Count how many values are strictly less than current savings for a proper rank
  const rank = allSavings.filter((v) => v < savings).length;
  const quartile = Math.floor((rank / allSavings.length) * 4);
  return ['$', '$$', '$$$', '$$$$'][Math.min(quartile, 3)];
};

/**
 * Get the background color for a benchmark status tag.
 *   BETTER → Status Colors/Green (#007A14)
 *   ON PAR → Gray Medium Fills (#71767A)
 *   WORSE  → Status Colors/Red (#D82727)
 *
 * @param {'BETTER' | 'ON PAR' | 'WORSE'} status
 * @returns {string} hex color
 */
export const getStatusColor = (status) => {
  switch (status) {
    case 'BETTER':
      return '#007A14';
    case 'WORSE':
      return '#D82727';
    case 'ON PAR':
    default:
      return '#71767A';
  }
};
